/* ============================================================
   Natural Roots — Product showcase (product page)
   Live product effects under the description. Each product page
   runs only its own effect:
   - mist: a spray burst from the nozzle (fluid mist cloud from the
     same engine as the bakhoor smoke + tiny glistening droplets),
     while the pump head presses down.
   - powder: a puff of powder from behind the jar lid that hangs,
     drifts and settles, with fine grains thrown up and falling back.
   - powder can also shift from one colour to another as the cloud rises
     (wasma: green powder turning indigo).
   - breeze: cool see-through air currents flowing past the jar.
   - smoke: the homepage bakhoor smoke, rising from behind the jar lid.
   - bubbles: iridescent soap bubbles rising around the bottle, foam at the
     base (2D canvas only).
   - silk: a satin drape behind the bottle, rippling while light slides
     over its folds (one shader).
   - strands: golden silk threads wrapping around the jar on slowly turning
     rings, with a glint sliding along each (2D canvas only).
   - swirl: a glossy paste surface behind the jar with a slowly turning
     swirl carved into it, light sliding over its ridges (same shader as silk).
   - water: the jar on a calm water surface, rippling reflection, rings
     spreading from its base and twinkling glints (2D only).
   - honey: a thread of honey beside the product forming a glossy drop that
     falls into a small pool on the pedestal (2D only).
   - dust: a warm sunbeam on the jar with fine powder motes drifting
     and glowing inside it (2D canvas only).
   Runs only while on screen, 30 fps on phones, one still frame for
   reduced motion.
   ============================================================ */
(function () {
  'use strict';
  if (window.NRShowcase) {
    window.NRShowcase.boot(document);
    return;
  }

  var FRAG_HEAD =
    '#ifdef GL_FRAGMENT_PRECISION_HIGH\n' +
    'precision highp float;\nprecision highp sampler2D;\n' +
    '#else\n' +
    'precision mediump float;\nprecision mediump sampler2D;\n' +
    '#endif\n' +
    'varying vec2 vUv;\nvarying vec2 vL;\nvarying vec2 vR;\nvarying vec2 vT;\nvarying vec2 vB;\n';

  var VERT =
    'precision highp float;\n' +
    'attribute vec2 aPosition;\n' +
    'varying vec2 vUv;\nvarying vec2 vL;\nvarying vec2 vR;\nvarying vec2 vT;\nvarying vec2 vB;\n' +
    'uniform vec2 texelSize;\n' +
    'void main () {\n' +
    '  vUv = aPosition * 0.5 + 0.5;\n' +
    '  vL = vUv - vec2(texelSize.x, 0.0);\n' +
    '  vR = vUv + vec2(texelSize.x, 0.0);\n' +
    '  vT = vUv + vec2(0.0, texelSize.y);\n' +
    '  vB = vUv - vec2(0.0, texelSize.y);\n' +
    '  gl_Position = vec4(aPosition, 0.0, 1.0);\n' +
    '}\n';

  var FS = {
    scale:
      'uniform sampler2D uTexture;\nuniform float value;\n' +
      'void main () { gl_FragColor = value * texture2D(uTexture, vUv); }\n',

    source:
      'uniform sampler2D uTarget;\nuniform float aspectRatio;\nuniform vec4 value;\n' +
      'uniform vec2 point;\nuniform float radius;\nuniform float blend;\n' +
      'void main () {\n' +
      '  vec2 p = vUv - point;\n' +
      '  p.x *= aspectRatio;\n' +
      '  float m = exp(-dot(p, p) / radius);\n' +
      '  vec4 base = texture2D(uTarget, vUv);\n' +
      '  gl_FragColor = mix(base + value * m, mix(base, value, m), blend);\n' +
      '}\n',

    advect:
      'uniform sampler2D uVelocity;\nuniform sampler2D uSource;\n' +
      'uniform vec2 uVelTexel;\nuniform vec2 uSrcTexel;\nuniform float dt;\nuniform float dissipation;\n' +
      'vec4 bilerp (sampler2D sam, vec2 uv, vec2 ts) {\n' +
      '  vec2 st = uv / ts - 0.5;\n' +
      '  vec2 i = floor(st);\n' +
      '  vec2 f = fract(st);\n' +
      '  vec4 a = texture2D(sam, (i + vec2(0.5, 0.5)) * ts);\n' +
      '  vec4 b = texture2D(sam, (i + vec2(1.5, 0.5)) * ts);\n' +
      '  vec4 c = texture2D(sam, (i + vec2(0.5, 1.5)) * ts);\n' +
      '  vec4 d = texture2D(sam, (i + vec2(1.5, 1.5)) * ts);\n' +
      '  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);\n' +
      '}\n' +
      'void main () {\n' +
      '#ifdef MANUAL_FILTERING\n' +
      '  vec2 coord = vUv - dt * bilerp(uVelocity, vUv, uVelTexel).xy * uVelTexel;\n' +
      '  vec4 result = bilerp(uSource, coord, uSrcTexel);\n' +
      '#else\n' +
      '  vec2 coord = vUv - dt * texture2D(uVelocity, vUv).xy * uVelTexel;\n' +
      '  vec4 result = texture2D(uSource, coord);\n' +
      '#endif\n' +
      '  gl_FragColor = result / (1.0 + dissipation * dt);\n' +
      '}\n',

    curl:
      'uniform sampler2D uVelocity;\n' +
      'void main () {\n' +
      '  float L = texture2D(uVelocity, vL).y;\n' +
      '  float R = texture2D(uVelocity, vR).y;\n' +
      '  float T = texture2D(uVelocity, vT).x;\n' +
      '  float B = texture2D(uVelocity, vB).x;\n' +
      '  gl_FragColor = vec4(0.5 * (R - L - T + B), 0.0, 0.0, 1.0);\n' +
      '}\n',

    /* vorticity confinement + buoyancy + breeze + curl-noise turbulence */
    forces:
      'uniform sampler2D uVelocity;\nuniform sampler2D uCurl;\nuniform sampler2D uDensity;\n' +
      'uniform float curl;\nuniform float buoyancy;\nuniform float dt;\nuniform float time;\n' +
      'uniform float wind;\nuniform float turbulence;\nuniform float aspect;\n' +
      'uniform float ambientDamp;\nuniform float maxSpeed;\nuniform float confineFloor;\n' +
      'float hash (vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }\n' +
      'float vnoise (vec2 p) {\n' +
      '  vec2 i = floor(p);\n  vec2 f = fract(p);\n  vec2 u = f * f * (3.0 - 2.0 * f);\n' +
      '  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);\n' +
      '}\n' +
      'void main () {\n' +
      '  float L = texture2D(uCurl, vL).x;\n' +
      '  float R = texture2D(uCurl, vR).x;\n' +
      '  float T = texture2D(uCurl, vT).x;\n' +
      '  float B = texture2D(uCurl, vB).x;\n' +
      '  float C = texture2D(uCurl, vUv).x;\n' +
      '  vec2 force = 0.5 * vec2(abs(T) - abs(B), abs(R) - abs(L));\n' +
      '  force /= length(force) + 0.0001;\n' +
      '  force *= curl * C;\n' +
      '  force.y *= -1.0;\n' +
      '  vec2 vel = texture2D(uVelocity, vUv).xy;\n' +
      '  float d = texture2D(uDensity, vUv).x;\n' +
      '  float smoke = smoothstep(0.02, 0.25, d);\n' +
      '  vel += force * dt * mix(confineFloor, 1.0, smoke);\n' +
      '  vel.y += buoyancy * d * dt;\n' +
      '  float h = smoothstep(0.12, 0.9, vUv.y);\n' +
      '  vec2 q = vec2(vUv.x * aspect, vUv.y) * 3.2 + vec2(time * 0.05, -time * 0.32);\n' +
      '  float e = 0.06;\n' +
      '  float n1 = vnoise(q + vec2(0.0, e));\n  float n2 = vnoise(q - vec2(0.0, e));\n' +
      '  float n3 = vnoise(q + vec2(e, 0.0));\n  float n4 = vnoise(q - vec2(e, 0.0));\n' +
      '  vec2 cn = vec2(n1 - n2, n4 - n3) / (2.0 * e);\n' +
      '  vel += (vec2(wind, 0.0) + cn * turbulence) * h * dt;\n' +
      '  vel /= 1.0 + dt * ambientDamp * (1.0 - smoke);\n' +
      '  float sp = length(vel);\n' +
      '  if (sp > maxSpeed) { vel *= maxSpeed / sp; }\n' +
      '  gl_FragColor = vec4(vel, 0.0, 1.0);\n' +
      '}\n',

    /* open top and sides, solid floor */
    divergence:
      'uniform sampler2D uVelocity;\n' +
      'void main () {\n' +
      '  float L = texture2D(uVelocity, vL).x;\n' +
      '  float R = texture2D(uVelocity, vR).x;\n' +
      '  float T = texture2D(uVelocity, vT).y;\n' +
      '  float B = texture2D(uVelocity, vB).y;\n' +
      '  vec2 C = texture2D(uVelocity, vUv).xy;\n' +
      '  if (vB.y < 0.0) { B = -C.y; }\n' +
      '  gl_FragColor = vec4(0.5 * (R - L + T - B), 0.0, 0.0, 1.0);\n' +
      '}\n',

    pressure:
      'uniform sampler2D uPressure;\nuniform sampler2D uDivergence;\n' +
      'void main () {\n' +
      '  float L = texture2D(uPressure, vL).x;\n' +
      '  float R = texture2D(uPressure, vR).x;\n' +
      '  float T = texture2D(uPressure, vT).x;\n' +
      '  float B = texture2D(uPressure, vB).x;\n' +
      '  if (vL.x < 0.0) { L = 0.0; }\n' +
      '  if (vR.x > 1.0) { R = 0.0; }\n' +
      '  if (vT.y > 1.0) { T = 0.0; }\n' +
      '  float div = texture2D(uDivergence, vUv).x;\n' +
      '  gl_FragColor = vec4((L + R + B + T - div) * 0.25, 0.0, 0.0, 1.0);\n' +
      '}\n',

    gradient:
      'uniform sampler2D uPressure;\nuniform sampler2D uVelocity;\nuniform float gradScale;\n' +
      'void main () {\n' +
      '  float L = texture2D(uPressure, vL).x;\n' +
      '  float R = texture2D(uPressure, vR).x;\n' +
      '  float T = texture2D(uPressure, vT).x;\n' +
      '  float B = texture2D(uPressure, vB).x;\n' +
      '  if (vL.x < 0.0) { L = 0.0; }\n' +
      '  if (vR.x > 1.0) { R = 0.0; }\n' +
      '  if (vT.y > 1.0) { T = 0.0; }\n' +
      '  vec2 vel = texture2D(uVelocity, vUv).xy;\n' +
      '  vel -= gradScale * vec2(R - L, T - B);\n' +
      '  gl_FragColor = vec4(vel, 0.0, 1.0);\n' +
      '}\n',

    /* density -> premultiplied colour with soft self-shading */
    display:
      'uniform sampler2D uTexture;\n' +
      'uniform vec3 colorLow;\nuniform vec3 colorHigh;\nuniform vec3 glowColor;\nuniform vec2 glowPos;\nuniform float glowSize;\n' +
      'uniform float intensity;\nuniform float shading;\nuniform float aspect;\nuniform float gamma;\nuniform vec4 edge;\nuniform vec2 colorRange;\n' +
      'void main () {\n' +
      '  float d = texture2D(uTexture, vUv).x;\n' +
      '  float dl = texture2D(uTexture, vL).x;\n' +
      '  float dr = texture2D(uTexture, vR).x;\n' +
      '  float dt = texture2D(uTexture, vT).x;\n' +
      '  float db = texture2D(uTexture, vB).x;\n' +
      '  vec3 n = normalize(vec3(dl - dr, db - dt, 0.08));\n' +
      '  float lit = clamp(dot(n, normalize(vec3(-0.25, -0.55, 0.8))) , 0.0, 1.0);\n' +
      '  float a = 1.0 - exp(-pow(max(d, 0.0), gamma) * intensity);\n' +
      '  vec3 col = mix(colorLow, colorHigh, smoothstep(colorRange.x, colorRange.y, vUv.y));\n' +
      '  vec2 g = vUv - glowPos;\n  g.x *= aspect;\n' +
      '  col = mix(col, glowColor, 0.55 * exp(-dot(g, g) / glowSize));\n' +
      '  col *= mix(1.0, 0.72 + 0.45 * lit, shading);\n' +
      '  a *= clamp(vUv.x / edge.x, 0.0, 1.0) * clamp((1.0 - vUv.x) / edge.y, 0.0, 1.0);\n' +
      '  a *= clamp(vUv.y / edge.z, 0.0, 1.0) * clamp((1.0 - vUv.y) / edge.w, 0.0, 1.0);\n' +
      '  gl_FragColor = vec4(col * a, a);\n' +
      '}\n'
  };

  /* ---------------------------------------------------------------- */
  /* WebGL helpers                                                    */
  /* ---------------------------------------------------------------- */
  function renderable(gl, internalFormat, format, type) {
    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, 4, 4, 0, format, type, null);
    var fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    var ok = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(fbo);
    gl.deleteTexture(tex);
    return ok;
  }

  function pickFormat(gl, isGL2, type, want) {
    if (!isGL2) return renderable(gl, gl.RGBA, gl.RGBA, type) ? { i: gl.RGBA, f: gl.RGBA } : null;
    var chain = { r: [[gl.R16F, gl.RED], [gl.RG16F, gl.RG], [gl.RGBA16F, gl.RGBA]],
                  rg: [[gl.RG16F, gl.RG], [gl.RGBA16F, gl.RGBA]] }[want];
    for (var k = 0; k < chain.length; k++) {
      if (renderable(gl, chain[k][0], chain[k][1], type)) return { i: chain[k][0], f: chain[k][1] };
    }
    return null;
  }

  function getContext(canvas) {
    var attrs = { alpha: true, depth: false, stencil: false, antialias: false, premultipliedAlpha: true, preserveDrawingBuffer: false };
    var gl = canvas.getContext('webgl2', attrs);
    var isGL2 = !!gl;
    if (!gl) gl = canvas.getContext('webgl', attrs) || canvas.getContext('experimental-webgl', attrs);
    if (!gl) return null;
    var type, linear;
    if (isGL2) {
      gl.getExtension('EXT_color_buffer_float');
      gl.getExtension('EXT_color_buffer_half_float');
      type = gl.HALF_FLOAT;
      linear = true;
    } else {
      var hf = gl.getExtension('OES_texture_half_float');
      if (!hf) return null;
      type = hf.HALF_FLOAT_OES;
      linear = !!gl.getExtension('OES_texture_half_float_linear');
    }
    var r = pickFormat(gl, isGL2, type, 'r');
    var rg = pickFormat(gl, isGL2, type, 'rg');
    if (!r || !rg) return null;
    return { gl: gl, type: type, linear: linear, r: r, rg: rg };
  }

  /* ---------------------------------------------------------------- */
  /* Smoke                                                            */
  /* ---------------------------------------------------------------- */
  function NRFluid(canvas, options) {
    var ctx = getContext(canvas);
    if (!ctx) throw new Error('nr-smoke: WebGL half-float not available');
    this.canvas = canvas;
    this.ctx = ctx;
    this.gl = ctx.gl;
    this.o = options || {};
    this.t = 0;
    this.running = false;
    this.slowFrames = 0;
    this.quality = 1;
    this.sources = [];
    this.pointer = null;
    this.tick = this.tick.bind(this);
    this.setup();
    this.resize();
  }

  NRFluid.prototype.setup = function () {
    var gl = this.gl;
    var kw = this.ctx.linear ? '' : '#define MANUAL_FILTERING\n';
    var vs = this.shader(gl.VERTEX_SHADER, VERT);
    var self = this;
    this.p = {};
    Object.keys(FS).forEach(function (name) {
      self.p[name] = self.program(vs, kw + FRAG_HEAD + FS[name]);
    });

    this.quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, -1, 1, 1, 1, 1, -1]), gl.STATIC_DRAW);
    this.index = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.index);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0, 1, 2, 0, 2, 3]), gl.STATIC_DRAW);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(0);
    gl.disable(gl.BLEND);
  };

  NRFluid.prototype.shader = function (type, src) {
    var gl = this.gl;
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
  };

  NRFluid.prototype.program = function (vs, fsSrc) {
    var gl = this.gl;
    var pr = gl.createProgram();
    gl.attachShader(pr, vs);
    gl.attachShader(pr, this.shader(gl.FRAGMENT_SHADER, fsSrc));
    gl.bindAttribLocation(pr, 0, 'aPosition');
    gl.linkProgram(pr);
    if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(pr));
    var u = {};
    var n = gl.getProgramParameter(pr, gl.ACTIVE_UNIFORMS);
    for (var i = 0; i < n; i++) {
      var name = gl.getActiveUniform(pr, i).name;
      u[name] = gl.getUniformLocation(pr, name);
    }
    return { id: pr, u: u };
  };

  NRFluid.prototype.fbo = function (w, h, fmt) {
    var gl = this.gl;
    var filter = this.ctx.linear ? gl.LINEAR : gl.NEAREST;
    gl.activeTexture(gl.TEXTURE0);
    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, fmt.i, w, h, 0, fmt.f, this.ctx.type, null);
    var fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    gl.viewport(0, 0, w, h);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    return { tex: tex, fb: fb, w: w, h: h, tx: 1 / w, ty: 1 / h };
  };

  NRFluid.prototype.double = function (w, h, fmt) {
    var a = this.fbo(w, h, fmt);
    var b = this.fbo(w, h, fmt);
    return { read: a, write: b, w: w, h: h, tx: 1 / w, ty: 1 / h,
      swap: function () { var t = this.read; this.read = this.write; this.write = t; } };
  };

  NRFluid.prototype.free = function () {
    var gl = this.gl;
    [this.velocity, this.dye, this.pressure].forEach(function (d) {
      if (!d) return;
      [d.read, d.write].forEach(function (f) { gl.deleteTexture(f.tex); gl.deleteFramebuffer(f.fb); });
    });
    [this.divergenceFbo, this.curlFbo].forEach(function (f) {
      if (f) { gl.deleteTexture(f.tex); gl.deleteFramebuffer(f.fb); }
    });
  };

  function res(base, w, h) {
    var ar = w / h;
    if (ar < 1) ar = 1 / ar;
    var min = Math.max(8, Math.round(base));
    var max = Math.round(base * ar);
    return w > h ? { w: max, h: min } : { w: min, h: max };
  }

  NRFluid.prototype.resize = function () {
    var rect = this.canvas.getBoundingClientRect();
    var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    var w = Math.max(2, Math.round(rect.width * dpr));
    var h = Math.max(2, Math.round(rect.height * dpr));
    if (w === this.cw && h === this.ch && this.velocity) return;
    this.cw = this.canvas.width = w;
    this.ch = this.canvas.height = h;
    this.aspect = w / h;
    var small = (window.innerWidth || rect.width) < 750;
    this.half = small;
    var simBase = (small ? 96 : 128) * (this.quality < 1 ? 0.75 : 1);
    var dyeBase = Math.min(Math.min(w, h), (small ? 420 : 640) * this.quality);
    if (!this.ctx.linear) dyeBase = Math.min(w, h);
    var sim = res(simBase, w, h);
    var dye = res(dyeBase, w, h);
    this.free();
    this.velocity = this.double(sim.w, sim.h, this.ctx.rg);
    this.pressure = this.double(sim.w, sim.h, this.ctx.r);
    this.divergenceFbo = this.fbo(sim.w, sim.h, this.ctx.r);
    this.curlFbo = this.fbo(sim.w, sim.h, this.ctx.r);
    this.dye = this.double(dye.w, dye.h, this.ctx.r);
    this.simH = sim.h;
  };

  /* sources: [{x, y}] in canvas uv (0..1, y up) */
  NRFluid.prototype.setSources = function (list) { this.sources = list || []; };

  NRFluid.prototype.use = function (prog) { this.gl.useProgram(prog.id); return prog.u; };

  NRFluid.prototype.bind = function (unit, target) {
    var gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, target.tex);
    return unit;
  };

  NRFluid.prototype.blit = function (target) {
    var gl = this.gl;
    if (target) {
      gl.viewport(0, 0, target.w, target.h);
      gl.bindFramebuffer(gl.FRAMEBUFFER, target.fb);
    } else {
      gl.viewport(0, 0, this.cw, this.ch);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }
    gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
  };

  NRFluid.prototype.splat = function (target, x, y, value, radius, blend) {
    var gl = this.gl;
    var u = this.use(this.p.source);
    gl.uniform2f(u.texelSize, target.tx, target.ty);
    gl.uniform1i(u.uTarget, this.bind(0, target.read));
    gl.uniform1f(u.aspectRatio, this.aspect);
    gl.uniform2f(u.point, x, y);
    gl.uniform4f(u.value, value[0], value[1], value[2], value[3]);
    gl.uniform1f(u.radius, radius);
    gl.uniform1f(u.blend, blend);
    this.blit(target.write);
    target.swap();
  };

  NRFluid.prototype.step = function (dt) {
    var gl = this.gl;
    var o = this.o;
    var amt = o.amount;
    var t = this.t;
    var v = this.velocity;
    var u;

    if (this.onStep) this.onStep(dt, t);
    /* emit: an effect can bring its own emitter; default = rising smoke */
    for (var i = 0; !this.emitter && i < this.sources.length; i++) {
      var s = this.sources[i];
      var ph = i * 1.7;
      var sway = Math.sin(t * 1.1 + ph) * 0.55 + Math.sin(t * 2.3 + ph * 2.1) * 0.3 + Math.sin(t * 5.7 + ph) * 0.15;
      var drift = Math.sin(t * 0.37 + ph) * 0.006;
      var puff = 0.78 + 0.22 * Math.sin(t * 0.9 + ph * 3.0) * Math.sin(t * 0.23 + ph);
      var r = o.radius * (s.w || 1);
      this.splat(v, s.x + drift, s.y, [sway * o.sway, o.lift * (0.9 + 0.1 * puff), 0, 0], r * 1.6, 1);
      this.splat(this.dye, s.x + drift, s.y, [amt * puff, 0, 0, 1], r, 1);
    }

    if (this.emitter) this.emitter(dt, t);

    /* hand through the smoke */
    if (this.pointer && this.pointer.moved) {
      var pt = this.pointer;
      this.splat(v, pt.x, pt.y, [pt.dx * o.stir, pt.dy * o.stir, 0, 0], 0.0035, 0);
      pt.moved = false;
    }

    u = this.use(this.p.curl);
    gl.uniform2f(u.texelSize, v.tx, v.ty);
    gl.uniform1i(u.uVelocity, this.bind(0, v.read));
    this.blit(this.curlFbo);

    u = this.use(this.p.forces);
    gl.uniform2f(u.texelSize, v.tx, v.ty);
    gl.uniform1i(u.uVelocity, this.bind(0, v.read));
    gl.uniform1i(u.uCurl, this.bind(1, this.curlFbo));
    gl.uniform1i(u.uDensity, this.bind(2, this.dye.read));
    gl.uniform1f(u.curl, o.curl);
    gl.uniform1f(u.buoyancy, o.buoyancy);
    gl.uniform1f(u.dt, dt);
    gl.uniform1f(u.time, t);
    gl.uniform1f(u.wind, (Math.sin(t * 0.13) * 0.7 + Math.sin(t * 0.051 + 2.0) * 0.3) * o.wind);
    gl.uniform1f(u.turbulence, o.turbulence);
    gl.uniform1f(u.aspect, this.aspect);
    gl.uniform1f(u.ambientDamp, o.ambientDamp === undefined ? 1.2 : o.ambientDamp);
    gl.uniform1f(u.maxSpeed, o.maxSpeed || (o.lift ? o.lift * 3 : 600));
    gl.uniform1f(u.confineFloor, o.confineFloor === undefined ? 0.25 : o.confineFloor);
    this.blit(v.write);
    v.swap();

    u = this.use(this.p.divergence);
    gl.uniform2f(u.texelSize, v.tx, v.ty);
    gl.uniform1i(u.uVelocity, this.bind(0, v.read));
    this.blit(this.divergenceFbo);

    u = this.use(this.p.scale);
    gl.uniform2f(u.texelSize, v.tx, v.ty);
    gl.uniform1i(u.uTexture, this.bind(0, this.pressure.read));
    gl.uniform1f(u.value, 0.8);
    this.blit(this.pressure.write);
    this.pressure.swap();

    u = this.use(this.p.pressure);
    gl.uniform2f(u.texelSize, v.tx, v.ty);
    gl.uniform1i(u.uDivergence, this.bind(0, this.divergenceFbo));
    var iters = (this.warming || this.quality < 1) ? 12 : 20;
    for (var k = 0; k < iters; k++) {
      gl.uniform1i(u.uPressure, this.bind(1, this.pressure.read));
      this.blit(this.pressure.write);
      this.pressure.swap();
    }

    u = this.use(this.p.gradient);
    gl.uniform2f(u.texelSize, v.tx, v.ty);
    gl.uniform1f(u.gradScale, o.gradScale || 0.5);
    gl.uniform1i(u.uPressure, this.bind(0, this.pressure.read));
    gl.uniform1i(u.uVelocity, this.bind(1, v.read));
    this.blit(v.write);
    v.swap();

    u = this.use(this.p.advect);
    gl.uniform2f(u.texelSize, v.tx, v.ty);
    gl.uniform2f(u.uVelTexel, v.tx, v.ty);
    gl.uniform2f(u.uSrcTexel, v.tx, v.ty);
    gl.uniform1i(u.uVelocity, this.bind(0, v.read));
    gl.uniform1i(u.uSource, this.bind(0, v.read));
    gl.uniform1f(u.dt, dt);
    gl.uniform1f(u.dissipation, o.velocityDissipation);
    this.blit(v.write);
    v.swap();

    gl.uniform2f(u.texelSize, this.dye.tx, this.dye.ty);
    gl.uniform2f(u.uSrcTexel, this.dye.tx, this.dye.ty);
    gl.uniform1i(u.uVelocity, this.bind(0, v.read));
    gl.uniform1i(u.uSource, this.bind(1, this.dye.read));
    gl.uniform1f(u.dissipation, o.densityDissipation);
    this.blit(this.dye.write);
    this.dye.swap();
  };

  NRFluid.prototype.render = function () {
    var gl = this.gl;
    var o = this.o;
    var u = this.use(this.p.display);
    gl.uniform2f(u.texelSize, this.dye.tx, this.dye.ty);
    gl.uniform1i(u.uTexture, this.bind(0, this.dye.read));
    gl.uniform3f(u.colorLow, o.colorLow[0], o.colorLow[1], o.colorLow[2]);
    gl.uniform3f(u.colorHigh, o.colorHigh[0], o.colorHigh[1], o.colorHigh[2]);
    gl.uniform3f(u.glowColor, o.glowColor[0], o.glowColor[1], o.glowColor[2]);
    gl.uniform2f(u.glowPos, this.glowPos ? this.glowPos[0] : 0.5, this.glowPos ? this.glowPos[1] : 0.5);
    gl.uniform1f(u.glowSize, o.glowSize);
    gl.uniform1f(u.aspect, this.aspect);
    gl.uniform1f(u.intensity, o.intensity);
    gl.uniform1f(u.shading, o.shading);
    gl.uniform1f(u.gamma, o.gamma || 1);
    var e = o.edge || [0.16, 0.16, 0.08, 0.26];
    var cr = this.colorRange || o.colorRange || [0.05, 0.85];
    gl.uniform2f(u.colorRange, cr[0], cr[1]);
    gl.uniform4f(u.edge, e[0], e[1], e[2], e[3]);
    this.blit(null);
    if (this.onRender) this.onRender();
  };

  NRFluid.prototype.advance = function (steps, dt) {
    for (var i = 0; i < steps; i++) { this.t += dt; this.step(dt); }
  };

  /* Pre-run the simulation a few steps per frame so the plume is already
     standing when the section scrolls in, without one long GPU stall. */
  NRFluid.prototype.warm = function (steps, perFrame, dt, done) {
    var self = this;
    var left = steps;
    self.warming = true;
    function run() {
      if (self.dead) return;
      var n = Math.min(perFrame, left);
      self.advance(n, dt);
      left -= n;
      if (left > 0) {
        self.warmRaf = requestAnimationFrame(run);
      } else {
        self.warming = false;
        self.render();
        if (done) done();
      }
    }
    run();
  };

  NRFluid.prototype.tick = function (now) {
    if (!this.running) return;
    if (this.half && (this.frame = (this.frame || 0) + 1) % 2) {
      this.raf = requestAnimationFrame(this.tick);
      return;
    }
    var dt = this.last ? (now - this.last) / 1000 : 1 / 60;
    this.last = now;
    if (dt > 0.034) this.slowFrames++;
    else if (this.slowFrames > 0) this.slowFrames -= 0.25;
    if (this.slowFrames > 90 && this.quality === 1) {
      this.quality = 0.6;
      this.cw = 0;
      this.resize();
    }
    dt = Math.min(Math.max(dt, 1 / 120), 1 / 30) * (this.o.timeScale || 1);
    this.t += dt;
    this.step(dt);
    this.render();
    this.raf = requestAnimationFrame(this.tick);
  };

  NRFluid.prototype.start = function () {
    if (this.running || this.warming || this.dead) return;
    this.running = true;
    this.last = 0;
    this.raf = requestAnimationFrame(this.tick);
  };

  NRFluid.prototype.stop = function () {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
  };

  NRFluid.prototype.destroy = function () {
    this.stop();
    this.dead = true;
    if (this.warmRaf) cancelAnimationFrame(this.warmRaf);
    this.free();
    var lose = this.gl.getExtension('WEBGL_lose_context');
    if (lose) lose.loseContext();
  };


  window.NRFluid = NRFluid;

  /* ---------------------------------------------------------------- */
  /* Droplets: tiny glistening drops thrown from the nozzle (2D)       */
  /* ---------------------------------------------------------------- */
  function gauss() {
    var u = 1 - Math.random();
    var v = Math.random();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  }

  function Drops(canvas, opt) {
    this.c = canvas;
    this.ctx = canvas.getContext('2d');
    this.p = [];
    this.o = opt;
    if (opt.from && opt.to) {
      this.pal = [];
      for (var i = 0; i < 8; i++) this.pal.push(rgbCss(mix(opt.from, opt.to, i / 7)));
    }
  }
  Drops.prototype.resize = function () {
    var r = this.c.getBoundingClientRect();
    var d = Math.min(window.devicePixelRatio || 1, 2);
    this.w = r.width;
    this.h = r.height;
    this.c.width = Math.max(2, Math.round(r.width * d));
    this.c.height = Math.max(2, Math.round(r.height * d));
    this.ctx.setTransform(d, 0, 0, d, 0, 0);
  };
  Drops.prototype.spawn = function (n, x, y, dx, dy, spread) {
    var o = this.o;
    var base = Math.atan2(dy, dx);
    for (var i = 0; i < n; i++) {
      var ang = base + gauss() * spread;
      var sp = this.w * (o.speed[0] + Math.random() * (o.speed[1] - o.speed[0]));
      this.p.push({
        x: x, y: y,
        vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp,
        life: 0, max: o.life[0] + Math.random() * (o.life[1] - o.life[0]),
        r: 0.3 + Math.pow(Math.random(), 2) * 0.95,
        g: Math.random() < o.glint
      });
    }
    if (this.p.length > 2400) this.p.splice(0, this.p.length - 2400);
  };
  Drops.prototype.update = function (dt) {
    var drag = Math.exp(-this.o.drag * dt);
    var grav = this.o.gravity * this.w;
    var keep = [];
    for (var i = 0; i < this.p.length; i++) {
      var q = this.p[i];
      q.vx *= drag;
      q.vy = q.vy * drag + grav * dt;
      q.x += q.vx * dt;
      q.y += q.vy * dt;
      q.life += dt;
      if (q.life < q.max) keep.push(q);
    }
    this.p = keep;
  };
  Drops.prototype.draw = function () {
    var ctx = this.ctx;
    var o = this.o;
    ctx.clearRect(0, 0, this.w, this.h);
    ctx.strokeStyle = o.color;
    ctx.fillStyle = o.color;
    ctx.lineCap = 'round';
    for (var i = 0; i < this.p.length; i++) {
      var q = this.p[i];
      var k = q.life / q.max;
      var a = Math.min(1, k / 0.06) * (1 - Math.max(0, (k - 0.45) / 0.55));
      ctx.globalAlpha = a * (q.g ? 0.9 : o.alpha);
      if (this.pal) ctx.fillStyle = this.pal[Math.min(7, (k * 9) | 0)];
      if (o.streak) {
        ctx.lineWidth = q.g ? q.r * 2.2 : q.r * 1.6;
        ctx.beginPath();
        ctx.moveTo(q.x - q.vx * o.streak, q.y - q.vy * o.streak);
        ctx.lineTo(q.x, q.y + 0.01);
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.arc(q.x, q.y, q.g ? q.r * 1.5 : q.r, 0, 6.2832);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  };

  /* ---------------------------------------------------------------- */
  /* Effects                                                          */
  /* ---------------------------------------------------------------- */
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function num(el, name, fallback) {
    var v = parseFloat(el.getAttribute(name));
    return isNaN(v) ? fallback : v;
  }

  function hexRgb(hex, fb) {
    var m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec((hex || '').trim());
    return m ? [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255] : fb;
  }
  function mix(c, t, k) { return [c[0] + (t[0] - c[0]) * k, c[1] + (t[1] - c[1]) * k, c[2] + (t[2] - c[2]) * k]; }
  function rgbCss(c) { return 'rgb(' + Math.round(c[0] * 255) + ',' + Math.round(c[1] * 255) + ',' + Math.round(c[2] * 255) + ')'; }
  function rnd(n) { var x = Math.sin(n * 12.9898 + 78.233) * 43758.5453; return x - Math.floor(x); }

  /* spray: a short sideways jet + fine streaking droplets; the pump head presses */
  function mistConfig(section, k) {
    return {
      press: true,
      still: 0.5,
      drops: { color: '#f3fbff', speed: [0.8, 1.65], life: [0.7, 1.8], drag: 3.1, gravity: 0.16, streak: 0.016, alpha: 0.42, glint: 0.06 },
      o: {
        amount: 0.028 + 0.036 * k, intensity: 1.5 + 1.1 * k, gamma: 1.12, radius: 0.0004,
        jetSpeed: 170, spread: 0.2, count: Math.round(420 + 520 * k),
        interval: 2.8, burst: 0.3, firstBurst: 0.35,
        lift: 0, sway: 0, buoyancy: -4, curl: 20, wind: 2, turbulence: 1.4, stir: 1400,
        velocityDissipation: 1.8, densityDissipation: 1.25, shading: 0.55, maxSpeed: 900,
        colorLow: [0.93, 0.97, 0.99], colorHigh: [0.9, 0.95, 0.97], glowColor: [1, 1, 1], glowSize: 0.00001,
        edge: [0.03, 0.12, 0.1, 0.12]
      },
      emit: function (fluid, o, env, local, n, dt, noz, dir, drops) {
        var dy = 0.015;
        for (var i = 0; i < 4; i++) {
          var L = i * 0.035;
          var x = noz.x + dir * L / fluid.aspect;
          var y = noz.y + dy * L;
          var r = o.radius * (1 + i * 3.0);
          var s = o.jetSpeed * env * (1 - i * 0.14);
          fluid.splat(fluid.velocity, x, y, [dir * s, dy * s, 0, 0], r * 1.6, 1);
          fluid.splat(fluid.dye, x, y, [o.amount * env * (1 - i * 0.2), 0, 0, 0], r, 0);
        }
        if (drops) drops.spawn(Math.round(o.count * dt / o.burst * env), noz.px + dir * 2, noz.py, dir, -dy, o.spread);
      }
    };
  }

  /* powder: an upward puff from behind the jar, then the cloud hangs, drifts and settles */
  function powderConfig(section, k) {
    var c = hexRgb(section.getAttribute('data-fx-color'), [0.66, 0.68, 0.37]);
    var c2 = hexRgb(section.getAttribute('data-fx-color2'), null);
    var grains = { color: rgbCss(mix(c, [1, 1, 1], 0.35)), speed: [0.7, 1.6], life: [1.3, 2.6], drag: 1.6, gravity: 0.8, streak: 0, alpha: 0.7, glint: 0.05 };
    if (c2) { grains.from = mix(c, [1, 1, 1], 0.3); grains.to = mix(c2, [1, 1, 1], 0.25); }
    return {
      press: false,
      shift: !!c2,
      still: 0.95,
      drops: grains,
      o: {
        amount: 0.085 + 0.06 * k, intensity: 2.0 + 1.3 * k, gamma: 1.05, radius: 0.0016,
        jetSpeed: 190, spread: 0.45, count: Math.round(180 + 260 * k),
        interval: 3.8, burst: 0.4, firstBurst: 0.3,
        lift: 0, sway: 0, buoyancy: 6, curl: 34, wind: 3, turbulence: 3, stir: 1400,
        velocityDissipation: 1.1, densityDissipation: c2 ? 0.75 : 0.4, shading: 0.8, maxSpeed: 600,
        colorLow: mix(c, [1, 1, 1], 0.28), colorHigh: c2 ? mix(c2, [1, 1, 1], 0.18) : mix(c, [1, 1, 1], 0.42), glowColor: [1, 1, 1], glowSize: 0.00001,
        edge: [0.1, 0.1, 0.06, 0.12]
      },
      emit: function (fluid, o, env, local, n, dt, noz, dir, drops) {
        var base = (rnd(n + 1) - 0.5) * 0.4;
        var fan = [-0.55, 0, 0.55];
        var pw = [0.55, 1, 0.55];
        for (var i = 0; i < 3; i++) {
          var ang = base + fan[i];
          var dx = Math.sin(ang);
          var dy = Math.cos(ang);
          var x = noz.x + dx * 0.03 / fluid.aspect;
          var y = noz.y + dy * 0.03;
          var r = o.radius * (i === 1 ? 1.6 : 1.1);
          var s = o.jetSpeed * env * pw[i];
          fluid.splat(fluid.velocity, x, y, [dx * s, dy * s, 0, 0], r * 1.4, 1);
          fluid.splat(fluid.dye, x, y, [o.amount * env * pw[i], 0, 0, 0], r, 0);
        }
        if (drops) drops.spawn(Math.round(o.count * dt / o.burst * env), noz.px, noz.py, Math.sin(base), -Math.cos(base), o.spread);
      }
    };
  }


  /* breeze: cool, see-through air currents flow in from the side behind the
     jar, curling gently, with a few glints carried along */
  function breezeConfig(section, k) {
    var c = hexRgb(section.getAttribute('data-fx-color'), [0.88, 0.97, 0.95]);
    return {
      press: false,
      continuous: true,
      warmSteps: 220,
      warmDt: 1 / 30,
      drops: { color: rgbCss(mix(c, [1, 1, 1], 0.5)), speed: [0.22, 0.45], life: [2.5, 4.5], drag: 0.25, gravity: -0.01, streak: 0.05, alpha: 0.5, glint: 0.25 },
      o: {
        amount: 0.16 + 0.12 * k, intensity: 1.9 + 1.2 * k, gamma: 1.55, radius: 0.00055,
        jetSpeed: 72, spread: 0.15, count: 0, interval: 1, burst: 1, firstBurst: 0,
        lift: 0, sway: 0, buoyancy: 1.5, curl: 26, wind: 5, turbulence: 4, stir: 1400,
        velocityDissipation: 0.3, densityDissipation: 0.62, shading: 0.6, maxSpeed: 400,
        colorLow: c, colorHigh: c, glowColor: [1, 1, 1], glowSize: 0.00001,
        edge: [0.16, 0.12, 0.1, 0.12]
      },
      emit: function (fluid, o, env, t, n, dt, noz, dir, drops) {
        var lanes = [0.54, 0.68, 0.82];
        var x0 = dir > 0 ? 0.03 : 0.97;
        for (var i = 0; i < 3; i++) {
          var ph = i * 2.1;
          var y = lanes[i] + Math.sin(t * 0.3 + ph) * 0.06;
          var w = Math.sin(t * 0.55 + ph * 1.7);
          var pulse = w > 0 ? w * w : 0;
          if (pulse < 0.02) continue;
          fluid.splat(fluid.velocity, x0, y, [dir * o.jetSpeed, Math.sin(t * 0.7 + ph) * 16, 0, 0], o.radius * 3.2, 1);
          fluid.splat(fluid.dye, x0, y, [o.amount * pulse, 0, 0, 0], o.radius, 1);
        }
        if (drops && Math.random() < dt * 6) {
          drops.spawn(1, dir > 0 ? 4 : drops.w - 4, drops.h * (0.25 + Math.random() * 0.5), dir, (Math.random() - 0.5) * 0.2, 0.15);
        }
      }
    };
  }


  /* smoke: the homepage bakhoor smoke, identical settings; it rises from
     behind the jar lid (built-in rising-smoke sources of the engine) */
  function smokeConfig(section, k) {
    return {
      press: false,
      continuous: true,
      native: true,
      warmSteps: 200,
      warmDt: 1 / 30,
      drops: null,
      o: {
        amount: 0.45 + 0.87 * k, intensity: 1.6 + 1.6 * k, gamma: 1.5, radius: 0.0005 + 0.0008 * k,
        lift: 40 + 20 * k, sway: 18, buoyancy: 18 + 10.7 * k, curl: 16, wind: 6, turbulence: 1.5, stir: 1400,
        velocityDissipation: 0.3, densityDissipation: 0.85 - 0.4 * k, shading: 0.85,
        colorLow: [0.97, 0.91, 0.82], colorHigh: [0.95, 0.95, 0.93], glowColor: [1.0, 0.78, 0.45], glowSize: 0.05,
        edge: [0.16, 0.16, 0.08, 0.26]
      },
      place: function (fluid, noz, source, W, H, wrap) {
        var spread = (source.offsetWidth * 0.05) / W;
        fluid.setSources([
          { x: noz.x - spread, y: noz.y, w: 1 },
          { x: noz.x + spread * 0.8, y: noz.y - 0.01, w: 0.8 }
        ]);
        fluid.glowPos = [noz.x, 1 - (source.offsetTop + source.offsetHeight * 0.05 - wrap.offsetTop) / H];
      }
    };
  }

  var CONFIGS = { mist: mistConfig, powder: powderConfig, breeze: breezeConfig, smoke: smokeConfig };

  /* honey: a thread of golden honey comes down beside the product; a glossy
     drop forms at its end, stretches, lets go and falls into a small pool on
     the pedestal, which ripples. Light glints on it and glows inside. 2D only. */
  function initHoney(section, onReady) {
    var front = section.querySelector('[data-nr-drops]');
    var source = section.querySelector('[data-nr-source]');
    if (!front || !source || num(section, 'data-level', 70) === 0) return null;
    var k = Math.max(0, Math.min(num(section, 'data-level', 70), 100)) / 100;
    var timeScale = Math.max(10, Math.min(num(section, 'data-speed', 60), 100)) / 100;
    var col = hexRgb(section.getAttribute('data-fx-color'), [0.88, 0.6, 0.16]);
    var cDark = rgbCss(mix(col, [0.3, 0.12, 0], 0.55));
    var cMid = rgbCss(col);
    var cBright = rgbCss(mix(col, [1, 0.93, 0.62], 0.55));
    var small = (window.innerWidth || 1000) < 750;
    var ctx = front.getContext('2d');
    var W = 0, H = 0, dx = 0, poolY = 0, Rmax = 0, w0 = 0, yHang = 0, g = 0, poolRx = 0;
    var T = 5.4, tDet = 2.9;
    var fx = { canvas: front, running: false, still: false, t: 2.5, o: { timeScale: timeScale } };

    function place() {
      var r = front.getBoundingClientRect();
      W = r.width;
      H = r.height;
      if (!W || !H) return;
      var d = Math.min(window.devicePixelRatio || 1, 2);
      front.width = Math.round(W * d);
      front.height = Math.round(H * d);
      ctx.setTransform(d, 0, 0, d, 0, 0);
      dx = source.offsetLeft + source.offsetWidth + W * 0.14;
      poolY = source.offsetTop + source.offsetHeight * 0.975;
      Rmax = W * (0.04 + 0.012 * k);
      w0 = Rmax * 0.45;
      yHang = H * 0.4;
      g = H * 2.4;
      poolRx = W * 0.075;
    }

    function fillFor(x, R) {
      var gr = ctx.createLinearGradient(x - R, 0, x + R, 0);
      gr.addColorStop(0, cDark);
      gr.addColorStop(0.3, cBright);
      gr.addColorStop(0.56, cMid);
      gr.addColorStop(1, cDark);
      return gr;
    }

    function drip(x, wTop, yNeck, wn, cy, R, ry) {
      ctx.beginPath();
      ctx.moveTo(x - wTop / 2, -2);
      ctx.bezierCurveTo(x - wTop / 2, yNeck * 0.7, x - wn / 2, yNeck - 8, x - wn / 2, yNeck);
      ctx.bezierCurveTo(x - wn / 2, yNeck + (cy - yNeck) * 0.5, x - R, cy - ry * 0.6, x - R, cy);
      ctx.ellipse(x, cy, R, ry, 0, Math.PI, 0, true);
      ctx.bezierCurveTo(x + R, cy - ry * 0.6, x + wn / 2, yNeck + (cy - yNeck) * 0.5, x + wn / 2, yNeck);
      ctx.bezierCurveTo(x + wn / 2, yNeck - 8, x + wTop / 2, yNeck * 0.7, x + wTop / 2, -2);
      ctx.closePath();
    }

    function shine(x, cy, R, ry) {
      var cg = ctx.createRadialGradient(x + R * 0.22, cy + ry * 0.35, 0, x + R * 0.22, cy + ry * 0.35, R * 0.7);
      cg.addColorStop(0, 'rgba(255,238,175,0.85)');
      cg.addColorStop(1, 'rgba(255,238,175,0)');
      ctx.globalAlpha = 1;
      ctx.fillStyle = cg;
      ctx.beginPath();
      ctx.ellipse(x, cy, R * 0.98, ry * 0.98, 0, 0, 6.2832);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,252,240,0.9)';
      ctx.beginPath();
      ctx.ellipse(x - R * 0.42, cy - ry * 0.38, R * 0.2, R * 0.11, -0.6, 0, 6.2832);
      ctx.fill();
      ctx.strokeStyle = cDark;
      ctx.globalAlpha = 0.35;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.ellipse(x, cy, R, ry, 0, 0, 6.2832);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    function render() {
      var c = fx.t % T;
      ctx.clearRect(0, 0, W, H);
      var R, cy, ry, wn;
      if (c < tDet - 0.7) {
        var q = c / (tDet - 0.7);
        R = Rmax * (0.35 + 0.65 * (1 - Math.pow(1 - q, 2)));
        ry = R * 1.12;
        cy = yHang + R * 0.15;
        wn = w0 * 0.9;
      } else if (c < tDet) {
        var q2 = (c - (tDet - 0.7)) / 0.7;
        R = Rmax;
        ry = R * (1.12 + 0.3 * q2);
        cy = yHang + R * 0.15 + Rmax * 1.5 * q2 * q2;
        wn = w0 * (0.9 - 0.72 * q2);
      } else {
        var q3 = (c - tDet) / (T - tDet);
        R = Rmax * (0.12 + 0.23 * q3);
        ry = R * 1.1;
        cy = yHang - Rmax * 0.6 * (1 - q3) + R * 0.15;
        wn = w0 * 0.85;
      }
      var yNeck = cy - ry * 0.95;
      /* thread + hanging drop */
      ctx.globalAlpha = 0.96;
      ctx.fillStyle = fillFor(dx, Math.max(R, w0));
      drip(dx, w0, yNeck, wn, cy, R, ry);
      ctx.fill();
      ctx.globalAlpha = 0.5;
      ctx.strokeStyle = 'rgba(255,250,235,0.9)';
      ctx.lineWidth = Math.max(1, w0 * 0.16);
      ctx.beginPath();
      ctx.moveTo(dx - w0 * 0.2, 0);
      ctx.lineTo(dx - wn * 0.2, yNeck);
      ctx.stroke();
      shine(dx, cy, R, ry);

      /* falling drop and the pool it lands in */
      var y0 = yHang + Rmax * 0.15 + Rmax * 1.5;
      var tImpact = Math.sqrt(Math.max(0, 2 * (poolY - y0) / g));
      var pulse = 0, ring = -1;
      if (c >= tDet) {
        var tau = c - tDet;
        if (tau < tImpact) {
          var yF = y0 + 0.5 * g * tau * tau;
          var v = g * tau;
          var st = 1 + Math.min(0.5, v / (H * 6));
          var rr = Rmax * 0.82;
          ctx.globalAlpha = 0.96;
          ctx.fillStyle = fillFor(dx, rr);
          ctx.beginPath();
          ctx.moveTo(dx, yF - rr * st * 1.5);
          ctx.bezierCurveTo(dx + rr * 0.2, yF - rr * st * 0.9, dx + rr, yF - rr * 0.4, dx + rr, yF);
          ctx.ellipse(dx, yF, rr, rr * st * 0.95, 0, 0, Math.PI, false);
          ctx.bezierCurveTo(dx - rr, yF - rr * 0.4, dx - rr * 0.2, yF - rr * st * 0.9, dx, yF - rr * st * 1.5);
          ctx.fill();
          shine(dx, yF, rr, rr * st * 0.95);
        } else {
          var ti = tau - tImpact;
          pulse = Math.exp(-ti * 3.2) * 0.18;
          ring = ti;
        }
      }
      var prx = poolRx * (1 + pulse);
      var pry = prx * 0.24;
      var pg = ctx.createRadialGradient(dx - prx * 0.2, poolY - pry * 0.3, 0, dx, poolY, prx);
      pg.addColorStop(0, cBright);
      pg.addColorStop(0.55, cMid);
      pg.addColorStop(1, cDark);
      ctx.globalAlpha = 0.95;
      ctx.fillStyle = pg;
      ctx.beginPath();
      ctx.ellipse(dx, poolY, prx, pry, 0, 0, 6.2832);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,252,240,0.75)';
      ctx.beginPath();
      ctx.ellipse(dx - prx * 0.35, poolY - pry * 0.35, prx * 0.22, pry * 0.18, 0, 0, 6.2832);
      ctx.fill();
      if (ring >= 0 && ring < 1.2) {
        ctx.globalAlpha = (1 - ring / 1.2) * 0.6;
        ctx.strokeStyle = cBright;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.ellipse(dx, poolY, prx * (0.4 + ring * 0.7), pry * (0.4 + ring * 0.7), 0, 0, 6.2832);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    var raf = null, last = 0, frame = 0;
    function tick(now) {
      if (!fx.running) return;
      raf = requestAnimationFrame(tick);
      if (small && (frame++ & 1)) return;
      var dt = last ? Math.min((now - last) / 1000, 1 / 20) : 1 / 60;
      last = now;
      fx.t += dt * timeScale;
      render();
    }
    fx.start = function () { if (fx.running) return; fx.running = true; last = 0; raf = requestAnimationFrame(tick); };
    fx.stop = function () { fx.running = false; if (raf) cancelAnimationFrame(raf); raf = null; };
    fx.destroy = fx.stop;
    fx.place = function () { place(); render(); };
    fx.render = render;
    fx.advance = function (steps, dt) { fx.t += steps * dt; };

    place();
    fx.still = reduceMotion || section.hasAttribute('data-still');
    render();
    section.classList.add('nr-show--canvas');
    section.setAttribute('data-ready', '');
    if (onReady) setTimeout(onReady, 0);
    return fx;
  }



  /* water: the jar stands on a calm water surface; its reflection ripples
     below it, rings spread from its base (back half behind the jar, front
     half in front) and small glints twinkle. 2D only. */
  function initWater(section, onReady) {
    var front = section.querySelector('[data-nr-drops]');
    var back = section.querySelector('[data-nr-beam]');
    var source = section.querySelector('[data-nr-source]');
    var img = source ? source.querySelector('img') : null;
    if (!front || !back || !source || !img || num(section, 'data-level', 70) === 0) return null;
    var k = Math.max(0, Math.min(num(section, 'data-level', 70), 100)) / 100;
    var timeScale = Math.max(10, Math.min(num(section, 'data-speed', 60), 100)) / 100;
    var col = hexRgb(section.getAttribute('data-fx-color'), [0.62, 0.84, 0.78]);
    var ringCol = rgbCss(mix(col, [1, 1, 1], 0.55));
    var tint = rgbCss(mix(col, [0, 0, 0], 0.72));
    var small = (window.innerWidth || 1000) < 750;
    var bottomFrac = 0.975;
    var fctx = front.getContext('2d');
    var bctx = back.getContext('2d');
    var W = 0, H = 0, cx = 0, jx = 0, jy = 0, jw = 0, jh = 0, baseY = 0;
    var glints = [];
    var fx = { canvas: front, running: false, still: false, t: 1.6, o: { timeScale: timeScale } };

    function place() {
      var r = front.getBoundingClientRect();
      W = r.width;
      H = r.height;
      if (!W || !H) return;
      var d = Math.min(window.devicePixelRatio || 1, 2);
      [front, back].forEach(function (c) {
        c.width = Math.round(W * d);
        c.height = Math.round(H * d);
        c.getContext('2d').setTransform(d, 0, 0, d, 0, 0);
      });
      jx = source.offsetLeft;
      jy = source.offsetTop;
      jw = source.offsetWidth;
      jh = source.offsetHeight;
      cx = jx + jw / 2;
      baseY = jy + jh * bottomFrac;
      glints = [];
      for (var i = 0; i < (small ? 14 : 22); i++) {
        var a = Math.random() * Math.PI;
        var rr = 0.35 + Math.random() * 0.65;
        glints.push({ x: cx + Math.cos(a) * W * 0.46 * rr, y: baseY + Math.sin(a) * (H - baseY) * 0.85 * rr, l: 3 + Math.random() * 8, ph: Math.random() * 6.283, f: 0.8 + Math.random() * 1.6 });
      }
    }

    function render() {
      var t = fx.t;
      fctx.clearRect(0, 0, W, H);
      bctx.clearRect(0, 0, W, H);
      var reflH = Math.min(H - baseY, jh * 0.95);
      bctx.save();
      bctx.beginPath();
      bctx.ellipse(cx, baseY, W * 0.5, reflH * 1.05, 0, 0, Math.PI);
      bctx.closePath();
      bctx.clip();
      var g = bctx.createLinearGradient(0, baseY, 0, baseY + reflH);
      g.addColorStop(0, 'rgba(' + Math.round(col[0] * 255) + ',' + Math.round(col[1] * 255) + ',' + Math.round(col[2] * 255) + ',0.16)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      bctx.fillStyle = g;
      bctx.fillRect(0, baseY, W, reflH * 1.1);
      /* the water plane fades out towards both sides */
      var hg = bctx.createLinearGradient(cx - W * 0.5, 0, cx + W * 0.5, 0);
      hg.addColorStop(0, 'rgba(0,0,0,0)');
      hg.addColorStop(0.32, 'rgba(0,0,0,1)');
      hg.addColorStop(0.68, 'rgba(0,0,0,1)');
      hg.addColorStop(1, 'rgba(0,0,0,0)');
      bctx.globalCompositeOperation = 'destination-in';
      bctx.fillStyle = hg;
      bctx.fillRect(0, baseY, W, reflH * 1.1);
      bctx.globalCompositeOperation = 'source-over';
      if (img.complete && img.naturalWidth) {
        var iw = img.naturalWidth, ih = img.naturalHeight;
        var step = small ? 3 : 2;
        var amp = 1.5 + 7 * k;
        for (var y = 0; y < reflH; y += step) {
          var q = y / reflH;
          var srcRow = ((baseY - jy) - y - step) / jh * ih;
          if (srcRow < 0) break;
          var off = (1 + amp * q) * Math.sin(y * 0.11 - t * 2.1) + amp * 0.4 * q * Math.sin(y * 0.031 + t * 1.3);
          bctx.globalAlpha = 0.55 * Math.pow(1 - q, 1.5);
          bctx.drawImage(img, 0, srcRow, iw, Math.max(1, step / jh * ih), jx + off, baseY + y, jw, step);
        }
      }
      bctx.globalAlpha = 0.45;
      bctx.globalCompositeOperation = 'source-atop';
      bctx.fillStyle = tint;
      bctx.fillRect(0, baseY, W, reflH * 1.1);
      bctx.globalCompositeOperation = 'source-over';
      bctx.restore();

      /* rings spreading from the base of the jar */
      var period = 2.8, life = 5.2;
      var nNow = Math.floor(t / period);
      for (var n = nNow; n > nNow - 3; n--) {
        var age = t - n * period;
        if (age < 0 || age > life) continue;
        var rx = jw * 0.5 + age * W * 0.085;
        var ry = rx * 0.15;
        var fade = Math.pow(1 - age / life, 1.3) * (0.4 + 0.4 * k);
        [[bctx, Math.PI, 2 * Math.PI, 0.55], [fctx, 0, Math.PI, 1]].forEach(function (pass) {
          var ctx = pass[0];
          ctx.strokeStyle = ringCol;
          ctx.lineWidth = 1.3;
          ctx.globalAlpha = fade * pass[3];
          ctx.beginPath();
          ctx.ellipse(cx, baseY, rx, ry, 0, pass[1], pass[2]);
          ctx.stroke();
          ctx.lineWidth = 0.8;
          ctx.globalAlpha = fade * pass[3] * 0.45;
          ctx.beginPath();
          ctx.ellipse(cx, baseY, rx * 0.93, ry * 0.93, 0, pass[1], pass[2]);
          ctx.stroke();
        });
      }
      /* glints on the water */
      fctx.strokeStyle = 'rgba(255,255,255,0.95)';
      fctx.lineWidth = 1;
      for (var i = 0; i < glints.length; i++) {
        var gl = glints[i];
        var a = Math.pow(Math.max(0, Math.sin(t * gl.f + gl.ph)), 4) * 0.8;
        if (a < 0.03) continue;
        fctx.globalAlpha = a;
        fctx.beginPath();
        fctx.moveTo(gl.x - gl.l / 2, gl.y);
        fctx.lineTo(gl.x + gl.l / 2, gl.y);
        fctx.stroke();
      }
      fctx.globalAlpha = 1;
      bctx.globalAlpha = 1;
    }

    var raf = null, last = 0, frame = 0;
    function tick(now) {
      if (!fx.running) return;
      raf = requestAnimationFrame(tick);
      if (small && (frame++ & 1)) return;
      var dt = last ? Math.min((now - last) / 1000, 1 / 20) : 1 / 60;
      last = now;
      fx.t += dt * timeScale;
      render();
    }
    fx.start = function () { if (fx.running) return; fx.running = true; last = 0; raf = requestAnimationFrame(tick); };
    fx.stop = function () { fx.running = false; if (raf) cancelAnimationFrame(raf); raf = null; };
    fx.destroy = fx.stop;
    fx.place = function () { place(); render(); };
    fx.render = render;
    fx.advance = function (steps, dt) { fx.t += steps * dt; };

    place();
    fx.still = reduceMotion || section.hasAttribute('data-still');
    if (!img.complete) img.addEventListener('load', function () { render(); }, { once: true });
    render();
    section.classList.add('nr-show--canvas');
    section.setAttribute('data-ready', '');
    if (onReady) setTimeout(onReady, 0);
    return fx;
  }







  /* strands: fine golden threads, like strands of hair, wrap around the jar
     on slowly turning rings (front half over the jar, back half behind it),
     with a glint of light sliding along each one. 2D only. */
  function initStrands(section, onReady) {
    var front = section.querySelector('[data-nr-drops]');
    var back = section.querySelector('[data-nr-beam]');
    var source = section.querySelector('[data-nr-source]');
    if (!front || !source || num(section, 'data-level', 70) === 0) return null;
    var k = Math.max(0, Math.min(num(section, 'data-level', 70), 100)) / 100;
    var timeScale = Math.max(10, Math.min(num(section, 'data-speed', 60), 100)) / 100;
    var col = hexRgb(section.getAttribute('data-fx-color'), [0.9, 0.76, 0.48]);
    var line = rgbCss(col);
    var glow = rgbCss(mix(col, [1, 0.98, 0.9], 0.7));
    var small = (window.innerWidth || 1000) < 750;
    var N = Math.round((small ? 8 : 10) + (small ? 5 : 7) * k);
    var fctx = front.getContext('2d');
    var bctx = back ? back.getContext('2d') : null;
    var W = 0, H = 0, cx = 0, top = 0, jw = 0, jh = 0;
    var strands = [];
    for (var i = 0; i < N; i++) {
      strands.push({
        f: 0.12 + Math.random() * 0.8,
        rx: 0.56 + Math.random() * 0.26,
        ry: 0.12 + Math.random() * 0.14,
        tilt: (Math.random() - 0.5) * 0.45,
        span: 2.0 + Math.random() * 2.4,
        ph: Math.random() * 6.283,
        sp: (0.16 + Math.random() * 0.24) * (Math.random() < 0.5 ? -1 : 1),
        wav: 0.008 + Math.random() * 0.016,
        wf: 2 + Math.random() * 3,
        w: 0.6 + Math.random() * 0.8,
        a: 0.35 + Math.random() * 0.45,
        g: Math.random()
      });
    }
    var fx = { canvas: front, running: false, still: false, t: 4, o: { timeScale: timeScale } };

    function place() {
      var r = front.getBoundingClientRect();
      W = r.width;
      H = r.height;
      if (!W || !H) return;
      var d = Math.min(window.devicePixelRatio || 1, 2);
      [front, back].forEach(function (c) {
        if (!c) return;
        c.width = Math.round(W * d);
        c.height = Math.round(H * d);
        c.getContext('2d').setTransform(d, 0, 0, d, 0, 0);
      });
      jw = source.offsetWidth;
      jh = source.offsetHeight;
      cx = source.offsetLeft + jw / 2;
      top = source.offsetTop;
    }

    var M = small ? 36 : 48;
    function render() {
      var t = fx.t;
      fctx.clearRect(0, 0, W, H);
      if (bctx) bctx.clearRect(0, 0, W, H);
      fctx.lineCap = 'round';
      if (bctx) bctx.lineCap = 'round';
      for (var i = 0; i < strands.length; i++) {
        var s0 = strands[i];
        var th0 = s0.ph + s0.sp * t;
        var yc = top + s0.f * jh;
        var breathe = 1 + 0.04 * Math.sin(t * 0.5 + s0.ph);
        var g = (t * 0.22 + s0.g) % 1;
        var px = 0, py = 0, pz = 0;
        for (var j = 0; j <= M; j++) {
          var u = j / M;
          var th = th0 + u * s0.span;
          var rr = jw * s0.rx * breathe * (1 + s0.wav * Math.sin(u * s0.wf * 6.283 + t * 0.8 + s0.ph));
          var x = cx + rr * Math.sin(th);
          var z = Math.cos(th);
          var y = yc + jw * s0.ry * z * Math.cos(s0.tilt) + (x - cx) * Math.sin(s0.tilt) * 0.35 + Math.sin(u * 3 + t * 0.6 + s0.ph) * 1.5;
          if (j > 0) {
            var isFront = (z + pz) > 0;
            var ctx = isFront || !bctx ? fctx : bctx;
            var ends = Math.sin(Math.PI * u);
            var near = Math.max(0, 1 - Math.abs(u - g) * 9);
            /* over the jar body the front threads turn see-through so the label stays readable */
            var overJar = isFront && Math.abs(x - cx) < jw * 0.47 && y > top && y < top + jh;
            var depthA = isFront ? (overJar ? 0.3 : 1) : 0.55;
            ctx.strokeStyle = line;
            ctx.globalAlpha = s0.a * ends * depthA * 0.2;
            ctx.lineWidth = s0.w + 2.6;
            ctx.beginPath();
            ctx.moveTo(px, py);
            ctx.lineTo(x, y);
            ctx.stroke();
            ctx.globalAlpha = s0.a * ends * depthA;
            ctx.lineWidth = s0.w;
            ctx.beginPath();
            ctx.moveTo(px, py);
            ctx.lineTo(x, y);
            ctx.stroke();
            if (near > 0) {
              ctx.globalAlpha = near * ends * 0.9 * depthA;
              ctx.strokeStyle = glow;
              ctx.lineWidth = s0.w + 1.1;
              ctx.beginPath();
              ctx.moveTo(px, py);
              ctx.lineTo(x, y);
              ctx.stroke();
            }
          }
          px = x;
          py = y;
          pz = z;
        }
      }
      fctx.globalAlpha = 1;
      if (bctx) bctx.globalAlpha = 1;
    }

    var raf = null, last = 0, frame = 0;
    function tick(now) {
      if (!fx.running) return;
      raf = requestAnimationFrame(tick);
      if (small && (frame++ & 1)) return;
      var dt = last ? Math.min((now - last) / 1000, 1 / 20) : 1 / 60;
      last = now;
      fx.t += dt * timeScale;
      render();
    }
    fx.start = function () { if (fx.running) return; fx.running = true; last = 0; raf = requestAnimationFrame(tick); };
    fx.stop = function () { fx.running = false; if (raf) cancelAnimationFrame(raf); raf = null; };
    fx.destroy = fx.stop;
    fx.place = function () { place(); render(); };
    fx.render = render;
    fx.advance = function (steps, dt) { fx.t += steps * dt; };

    place();
    fx.still = reduceMotion || section.hasAttribute('data-still');
    render();
    section.classList.add('nr-show--canvas');
    section.setAttribute('data-ready', '');
    if (onReady) setTimeout(onReady, 0);
    return fx;
  }



  /* silk: a satin drape behind the bottle, rippling slowly while the light
     slides over its folds. One fragment shader, no simulation. */
  var SILK_FS =
    'precision mediump float;\n' +
    'varying vec2 vUv;\n' +
    'uniform float time;\nuniform float aspect;\nuniform vec3 base;\nuniform vec3 hi;\nuniform float amp;\nuniform vec2 ctr;\nuniform float mode;\nuniform vec2 sc;\n' +
    'float hgt(vec2 p, float t) {\n' +
    '  vec2 q = p;\n' +
    '  q.x += 0.32 * sin(p.y * 1.5 + t * 0.35);\n' +
    '  q.y += 0.2 * sin(p.x * 1.2 - t * 0.28);\n' +
    '  float f = 0.6 * sin(q.x * 2.6 + q.y * 1.1 + t * 0.55);\n' +
    '  f += 0.32 * sin(q.x * 4.3 - q.y * 1.9 - t * 0.38 + 1.3);\n' +
    '  f += 0.14 * sin(q.x * 7.9 + q.y * 3.1 + t * 0.7 + 2.1);\n' +
    '  return f * amp;\n' +
    '}\n' +
    'float swirlH(vec2 p, float t) {\n' +
    '  vec2 d = p - vec2(sc.x * aspect, sc.y);\n' +
    '  float r = length(d);\n' +
    '  float a = atan(d.y, d.x);\n' +
    '  float f = 0.65 * sin(a * 3.0 + r * 15.0 - t * 0.5);\n' +
    '  f += 0.15 * sin(a * 5.0 - r * 24.0 + t * 0.32);\n' +
    '  return f * amp * 0.055 * smoothstep(0.0, 0.1, r);\n' +
    '}\n' +
    'void main () {\n' +
    '  vec3 n;\n' +
    '  float sp;\n' +
    '  if (mode > 0.5) {\n' +
    '    vec2 q = vec2(vUv.x * aspect, vUv.y);\n' +
    '    float s0 = swirlH(q, time);\n' +
    '    float sx = (swirlH(q + vec2(0.002, 0.0), time) - s0) / 0.002;\n' +
    '    float sy = (swirlH(q + vec2(0.0, 0.002), time) - s0) / 0.002;\n' +
    '    n = normalize(vec3(-sx, -sy, 1.0));\n' +
    '  } else {\n' +
    '    vec2 p = vec2(vUv.x * aspect, vUv.y) * 3.8;\n' +
    '    float e = 0.004;\n' +
    '    float h0 = hgt(p, time);\n' +
    '    float hx = (hgt(p + vec2(e, 0.0), time) - h0) / e;\n' +
    '    float hy = (hgt(p + vec2(0.0, e), time) - h0) / e;\n' +
    '    n = normalize(vec3(-hx * 0.17, -hy * 0.17, 1.0));\n' +
    '  }\n' +
    '  vec3 L = normalize(vec3(-0.45, 0.55, 0.7));\n' +
    '  vec3 H = normalize(L + vec3(0.0, 0.0, 1.0));\n' +
    '  float ndl = clamp(dot(n, L), 0.0, 1.0);\n' +
    '  float ndh = clamp(dot(n, H), 0.0, 1.0);\n' +
    '  if (mode > 0.5) { sp = 0.85 * pow(ndh, 60.0) + 0.2 * pow(ndh, 10.0); }\n' +
    '  else { sp = 0.24 * pow(ndh, 18.0) + 0.7 * pow(ndh, 110.0); }\n' +
    '  vec3 c = base * (0.16 + 0.72 * ndl) + hi * sp;\n' +
    '  vec2 d = (vUv - ctr) * vec2(1.0, 1.15);\n' +
    '  float m = smoothstep(0.66, 0.2, length(d)) * smoothstep(0.0, 0.18, vUv.y);\n' +
    '  gl_FragColor = vec4(c * m, m);\n' +
    '}\n';

  function initSilk(section, onReady) {
    var canvas = section.querySelector('[data-nr-fluid]');
    var source = section.querySelector('[data-nr-source]');
    if (!canvas || !source || num(section, 'data-level', 70) === 0) return null;
    var k = Math.max(0, Math.min(num(section, 'data-level', 70), 100)) / 100;
    var timeScale = Math.max(10, Math.min(num(section, 'data-speed', 60), 100)) / 100;
    var mode = section.getAttribute('data-effect') === 'swirl' ? 1 : 0;
    var col = hexRgb(section.getAttribute('data-fx-color'), mode ? [0.42, 0.38, 0.18] : [0.84, 0.74, 0.58]);
    var hi = mix(col, [1, 0.97, 0.9], mode ? 0.75 : 0.6);
    var small = (window.innerWidth || 1000) < 750;
    var attrs = { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false };
    var gl = canvas.getContext('webgl', attrs) || canvas.getContext('experimental-webgl', attrs);
    if (!gl) { section.classList.add('nr-show--no-webgl'); return null; }
    function sh(type, src) {
      var x = gl.createShader(type);
      gl.shaderSource(x, src);
      gl.compileShader(x);
      if (!gl.getShaderParameter(x, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(x));
      return x;
    }
    var pr = gl.createProgram();
    try {
      gl.attachShader(pr, sh(gl.VERTEX_SHADER, 'attribute vec2 p;\nvarying vec2 vUv;\nvoid main () { vUv = p * 0.5 + 0.5; gl_Position = vec4(p, 0.0, 1.0); }\n'));
      gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, SILK_FS));
      gl.bindAttribLocation(pr, 0, 'p');
      gl.linkProgram(pr);
      if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) throw new Error('link');
    } catch (err) {
      section.classList.add('nr-show--no-webgl');
      return null;
    }
    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(0);
    gl.useProgram(pr);
    var U = {};
    ['time', 'aspect', 'base', 'hi', 'amp', 'ctr', 'mode', 'sc'].forEach(function (n) { U[n] = gl.getUniformLocation(pr, n); });
    var aspect = 1, ctr = [0.5, 0.45], sc = [0.5, 0.6];
    var fx = { canvas: canvas, running: false, still: false, t: 3, o: { timeScale: timeScale } };

    function place() {
      var r = canvas.getBoundingClientRect();
      if (!r.width || !r.height) return;
      var d = Math.min(window.devicePixelRatio || 1, small ? 1.25 : 1.5);
      canvas.width = Math.round(r.width * d);
      canvas.height = Math.round(r.height * d);
      gl.viewport(0, 0, canvas.width, canvas.height);
      aspect = canvas.width / canvas.height;
      var wrap = canvas.parentNode;
      ctr = [(source.offsetLeft + source.offsetWidth / 2 - wrap.offsetLeft) / wrap.offsetWidth, mode ? 0.5 : 0.45];
      sc = [ctr[0], Math.min(0.9, 1 - (source.offsetTop - wrap.offsetTop) / wrap.offsetHeight + 0.2)];
    }
    function render() {
      gl.uniform1f(U.time, fx.t);
      gl.uniform1f(U.aspect, aspect);
      gl.uniform3f(U.base, col[0], col[1], col[2]);
      gl.uniform3f(U.hi, hi[0], hi[1], hi[2]);
      gl.uniform1f(U.amp, 0.55 + 0.6 * k);
      gl.uniform2f(U.ctr, ctr[0], ctr[1]);
      gl.uniform1f(U.mode, mode);
      gl.uniform2f(U.sc, sc[0], sc[1]);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    var raf = null, last = 0, frame = 0;
    function tick(now) {
      if (!fx.running) return;
      raf = requestAnimationFrame(tick);
      if (small && (frame++ & 1)) return;
      var dt = last ? Math.min((now - last) / 1000, 1 / 20) : 1 / 60;
      last = now;
      fx.t += dt * timeScale;
      render();
    }
    fx.start = function () { if (fx.running) return; fx.running = true; last = 0; raf = requestAnimationFrame(tick); };
    fx.stop = function () { fx.running = false; if (raf) cancelAnimationFrame(raf); raf = null; };
    fx.destroy = function () { fx.stop(); var x = gl.getExtension('WEBGL_lose_context'); if (x) x.loseContext(); };
    fx.place = function () { place(); render(); };
    fx.render = render;
    fx.advance = function (steps, dt) { fx.t += steps * dt; };
    fx.gl = gl;

    place();
    fx.still = reduceMotion || section.hasAttribute('data-still');
    render();
    section.classList.add('nr-show--webgl');
    section.setAttribute('data-ready', '');
    if (onReady) setTimeout(onReady, 0);
    return fx;
  }



  /* bubbles: soap bubbles with iridescent rims rise around the bottle (some in
     front, some behind), sway, and now and then pop; soft foam at the base.
     2D only. */
  function initBubbles(section, onReady) {
    var front = section.querySelector('[data-nr-drops]');
    var back = section.querySelector('[data-nr-beam]');
    var source = section.querySelector('[data-nr-source]');
    if (!front || !source || num(section, 'data-level', 70) === 0) return null;
    var k = Math.max(0, Math.min(num(section, 'data-level', 70), 100)) / 100;
    var timeScale = Math.max(10, Math.min(num(section, 'data-speed', 60), 100)) / 100;
    var small = (window.innerWidth || 1000) < 750;
    var count = Math.round((small ? 12 : 18) + (small ? 10 : 16) * k);
    var fctx = front.getContext('2d');
    var bctx = back ? back.getContext('2d') : null;
    var W = 0, H = 0, cx = 0, baseY = 0, bw = 0, bh = 0;
    var bubbles = [], foam = [], pops = [];

    function sprite(shift) {
      var c = document.createElement('canvas');
      c.width = c.height = 128;
      var g = c.getContext('2d');
      var R = 60, x = 64, y = 64;
      var body = g.createRadialGradient(x, y, R * 0.15, x, y, R);
      body.addColorStop(0, 'rgba(255,255,255,0.015)');
      body.addColorStop(0.72, 'rgba(255,255,255,0.05)');
      body.addColorStop(0.93, 'rgba(255,255,255,0.2)');
      body.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = body;
      g.beginPath();
      g.arc(x, y, R, 0, 6.2832);
      g.fill();
      var film = g.createLinearGradient(x - R, y - R, x + R, y + R);
      film.addColorStop(0, 'hsla(' + ((300 + shift) % 360) + ',90%,70%,0.16)');
      film.addColorStop(0.35, 'hsla(' + ((45 + shift) % 360) + ',95%,66%,0.12)');
      film.addColorStop(0.65, 'hsla(' + ((170 + shift) % 360) + ',85%,62%,0.12)');
      film.addColorStop(1, 'hsla(' + ((225 + shift) % 360) + ',90%,70%,0.16)');
      g.save();
      g.beginPath();
      g.arc(x, y, R - 1, 0, 6.2832);
      g.clip();
      g.fillStyle = film;
      g.fillRect(0, 0, 128, 128);
      g.restore();
      /* thin-film colours: soft bands of magenta, gold, green and cyan along the rim */
      var bands = [[300, 0.9], [45, 0.8], [150, 0.65], [190, 0.85], [280, 0.75]];
      for (var a = 0; a < 360; a += 4) {
        var t = (a + shift) % 360;
        var bi = Math.floor(t / 72) % bands.length;
        var hue = bands[bi][0] + Math.sin(t * 0.05) * 18;
        var al = bands[bi][1] * (0.55 + 0.45 * Math.sin(t * 0.09 + shift));
        g.strokeStyle = 'hsla(' + hue + ',95%,68%,' + al.toFixed(3) + ')';
        g.lineWidth = 4;
        g.beginPath();
        g.arc(x, y, R - 2.5, a * Math.PI / 180, (a + 5) * Math.PI / 180);
        g.stroke();
        g.strokeStyle = 'hsla(' + ((hue + 140) % 360) + ',90%,70%,' + (al * 0.45).toFixed(3) + ')';
        g.lineWidth = 6;
        g.beginPath();
        g.arc(x, y, R - 8, a * Math.PI / 180, (a + 5) * Math.PI / 180);
        g.stroke();
      }
      g.fillStyle = 'rgba(255,255,255,0.85)';
      g.beginPath();
      g.ellipse(x - R * 0.38, y - R * 0.42, R * 0.22, R * 0.09, -0.75, 0, 6.2832);
      g.fill();
      g.fillStyle = 'rgba(255,255,255,0.35)';
      g.beginPath();
      g.ellipse(x + R * 0.44, y + R * 0.4, R * 0.1, R * 0.04, -0.75, 0, 6.2832);
      g.fill();
      return c;
    }
    var sprites = [sprite(0), sprite(90), sprite(180), sprite(270)];

    function spawn(b, initial) {
      b.depth = Math.random() < 0.42 ? 0 : 1;
      b.r = b.depth ? 7 + Math.random() * 20 : 5 + Math.random() * 11;
      var side = Math.random() < 0.5 ? -1 : 1;
      b.x = cx + side * (bw * 0.55 + Math.random() * W * 0.3);
      b.y = initial ? H * (0.12 + Math.random() * 0.75) : baseY - Math.random() * 12;
      b.vy = -(16 + Math.random() * 24) * (b.depth ? 1 : 0.75);
      b.ph = Math.random() * 6.283;
      b.wob = 6 + Math.random() * 12;
      b.s = (Math.random() * 4) | 0;
      b.rot = Math.random() * 6.283;
      b.rs = (Math.random() - 0.5) * 0.6;
      b.life = initial ? Math.random() * 3 : 0;
      b.max = 4.5 + Math.random() * 5;
    }

    function place() {
      var r = front.getBoundingClientRect();
      W = r.width;
      H = r.height;
      if (!W || !H) return;
      var d = Math.min(window.devicePixelRatio || 1, 2);
      [front, back].forEach(function (c) {
        if (!c) return;
        c.width = Math.round(W * d);
        c.height = Math.round(H * d);
        c.getContext('2d').setTransform(d, 0, 0, d, 0, 0);
      });
      bw = source.offsetWidth;
      bh = source.offsetHeight;
      cx = source.offsetLeft + bw / 2;
      baseY = source.offsetTop + bh * 0.985;
      foam = [];
      for (var i = 0; i < (small ? 34 : 46); i++) {
        var a = Math.random() * 6.283;
        var rr = Math.sqrt(Math.random());
        foam.push({ x: cx + Math.cos(a) * rr * bw * 0.95, y: baseY + Math.sin(a) * rr * bw * 0.12, r: 2 + Math.random() * 6, s: (Math.random() * 4) | 0, ph: Math.random() * 6.283 });
      }
      foam.sort(function (p, q) { return p.y - q.y; });
      if (!bubbles.length) {
        for (var j = 0; j < count; j++) { var b = {}; spawn(b, true); bubbles.push(b); }
      }
    }

    var fx = { canvas: front, running: false, still: false, t: 0, o: { timeScale: timeScale } };

    function update(dt) {
      fx.t += dt;
      var t = fx.t;
      for (var i = 0; i < bubbles.length; i++) {
        var b = bubbles[i];
        b.life += dt;
        b.y += b.vy * dt;
        b.x += Math.sin(t * 1.2 + b.ph) * b.wob * dt;
        b.rot += b.rs * dt;
        if (b.y < -b.r * 2 || b.life > b.max) {
          if (b.y > b.r && Math.random() < 0.8) pops.push({ x: b.x, y: b.y, r: b.r, t: 0, depth: b.depth });
          spawn(b, false);
        }
      }
      for (var j = pops.length - 1; j >= 0; j--) {
        pops[j].t += dt;
        if (pops[j].t > 0.3) pops.splice(j, 1);
      }
    }

    function drawBubble(ctx, b, alpha) {
      ctx.globalAlpha = alpha;
      ctx.save();
      ctx.translate(b.x, b.y);
      ctx.rotate(b.rot);
      ctx.drawImage(sprites[b.s], -b.r, -b.r, b.r * 2, b.r * 2);
      ctx.restore();
    }

    function drawFoam(ctx, frontHalf) {
      var t = fx.t;
      for (var i = 0; i < foam.length; i++) {
        var f = foam[i];
        if ((f.y >= baseY) !== frontHalf) continue;
        var r = f.r * (1 + 0.06 * Math.sin(t * 2 + f.ph));
        ctx.globalAlpha = frontHalf ? 0.75 : 0.45;
        ctx.drawImage(sprites[f.s], f.x - r, f.y - r, r * 2, r * 2);
      }
    }

    function render() {
      fctx.clearRect(0, 0, W, H);
      if (bctx) bctx.clearRect(0, 0, W, H);
      var haze = fctx.createRadialGradient(cx, baseY, 0, cx, baseY, bw * 1.05);
      haze.addColorStop(0, 'rgba(255,255,255,0.18)');
      haze.addColorStop(1, 'rgba(255,255,255,0)');
      fctx.globalAlpha = 1;
      fctx.fillStyle = haze;
      fctx.beginPath();
      fctx.ellipse(cx, baseY, bw * 1.05, bw * 0.16, 0, 0, 6.2832);
      fctx.fill();
      if (bctx) drawFoam(bctx, false);
      drawFoam(fctx, true);
      for (var i = 0; i < bubbles.length; i++) {
        var b = bubbles[i];
        var fade = Math.min(1, b.life / 0.6) * Math.min(1, (b.max - b.life) / 0.6 + 0.4);
        if (b.depth) drawBubble(fctx, b, 0.95 * fade);
        else if (bctx) drawBubble(bctx, b, 0.55 * fade);
      }
      for (var j = 0; j < pops.length; j++) {
        var p = pops[j];
        var ctx = p.depth || !bctx ? fctx : bctx;
        var q = p.t / 0.3;
        ctx.globalAlpha = (1 - q) * 0.55;
        ctx.strokeStyle = 'rgba(255,255,255,0.9)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * (1 + q * 0.9), 0, 6.2832);
        ctx.stroke();
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        for (var m = 0; m < 5; m++) {
          var a = m * 1.2566 + p.r;
          ctx.fillRect(p.x + Math.cos(a) * p.r * (1 + q * 2.2), p.y + Math.sin(a) * p.r * (1 + q * 2.2), 1.4, 1.4);
        }
      }
      fctx.globalAlpha = 1;
      if (bctx) bctx.globalAlpha = 1;
    }

    var raf = null, last = 0, frame = 0;
    function tick(now) {
      if (!fx.running) return;
      raf = requestAnimationFrame(tick);
      if (small && (frame++ & 1)) return;
      var dt = last ? Math.min((now - last) / 1000, 1 / 20) : 1 / 60;
      last = now;
      update(dt * timeScale);
      render();
    }
    fx.start = function () { if (fx.running) return; fx.running = true; last = 0; raf = requestAnimationFrame(tick); };
    fx.stop = function () { fx.running = false; if (raf) cancelAnimationFrame(raf); raf = null; };
    fx.destroy = fx.stop;
    fx.place = function () { var had = bubbles.length; place(); if (had) render(); };
    fx.render = render;
    fx.advance = function (steps, dt) { for (var i = 0; i < steps; i++) update(dt); };

    place();
    fx.still = reduceMotion || section.hasAttribute('data-still');
    update(0.01);
    render();
    section.classList.add('nr-show--canvas');
    section.setAttribute('data-ready', '');
    if (onReady) setTimeout(onReady, 0);
    return fx;
  }



  /* dust: a warm sunbeam falls on the jar; fine powder motes drift in it,
     lit only while they cross the light. 2D only, no WebGL. */
  function initDust(section, onReady) {
    var canvas = section.querySelector('[data-nr-drops]');
    var beamCanvas = section.querySelector('[data-nr-beam]');
    var source = section.querySelector('[data-nr-source]');
    if (!canvas || !source || num(section, 'data-level', 70) === 0) return null;
    var k = Math.max(0, Math.min(num(section, 'data-level', 70), 100)) / 100;
    var col = hexRgb(section.getAttribute('data-fx-color'), [0.9, 0.86, 0.66]);
    var dotCol = mix(col, [1, 0.97, 0.9], 0.45);
    var timeScale = Math.max(10, Math.min(num(section, 'data-speed', 60), 100)) / 100;
    var nx = num(section, 'data-nozzle-x', 50) / 100;
    var ny = num(section, 'data-nozzle-y', 8) / 100;
    var ctx = canvas.getContext('2d');
    var small = (window.innerWidth || 1000) < 750;
    var count = Math.round((small ? 90 : 130) + (small ? 110 : 170) * k);
    var W = 0, H = 0, C, P0, u, n, Lb, w0, w1, jarW;
    var motes = [];
    var sprite = document.createElement('canvas');
    sprite.width = sprite.height = 32;
    var sg = sprite.getContext('2d');
    var grd = sg.createRadialGradient(16, 16, 0, 16, 16, 16);
    grd.addColorStop(0, rgbCss(dotCol));
    grd.addColorStop(0.35, 'rgba(' + Math.round(dotCol[0] * 255) + ',' + Math.round(dotCol[1] * 255) + ',' + Math.round(dotCol[2] * 255) + ',0.55)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    sg.fillStyle = grd;
    sg.fillRect(0, 0, 32, 32);

    var fx = { canvas: canvas, running: false, still: false, t: 0, o: { timeScale: timeScale } };

    function drawBeam() {
      if (!beamCanvas) return;
      var d = Math.min(window.devicePixelRatio || 1, 1.5);
      beamCanvas.width = Math.round(W * d);
      beamCanvas.height = Math.round(H * d);
      var b = beamCanvas.getContext('2d');
      b.setTransform(d, 0, 0, d, 0, 0);
      b.clearRect(0, 0, W, H);
      var P1 = { x: P0.x + u.x * Lb * 0.18, y: P0.y + u.y * Lb * 0.18 };
      var m = { x: (C.x + P1.x) / 2, y: (C.y + P1.y) / 2 };
      var wm = (w0 + w1) / 2;
      var g = b.createLinearGradient(m.x - n.x * wm * 1.3, m.y - n.y * wm * 1.3, m.x + n.x * wm * 1.3, m.y + n.y * wm * 1.3);
      g.addColorStop(0, 'rgba(255,236,196,0)');
      g.addColorStop(0.5, 'rgba(255,236,196,0.27)');
      g.addColorStop(1, 'rgba(255,236,196,0)');
      b.fillStyle = g;
      b.beginPath();
      b.moveTo(C.x + n.x * w0 * 1.3, C.y + n.y * w0 * 1.3);
      b.lineTo(C.x - n.x * w0 * 1.3, C.y - n.y * w0 * 1.3);
      b.lineTo(P1.x - n.x * w1 * 1.3, P1.y - n.y * w1 * 1.3);
      b.lineTo(P1.x + n.x * w1 * 1.3, P1.y + n.y * w1 * 1.3);
      b.closePath();
      b.fill();
      b.globalCompositeOperation = 'destination-in';
      var f = b.createLinearGradient(C.x, C.y, P1.x, P1.y);
      f.addColorStop(0, 'rgba(0,0,0,0.35)');
      f.addColorStop(0.45, 'rgba(0,0,0,1)');
      f.addColorStop(0.85, 'rgba(0,0,0,0.75)');
      f.addColorStop(1, 'rgba(0,0,0,0)');
      b.fillStyle = f;
      b.fillRect(0, 0, W, H);
      b.globalCompositeOperation = 'source-over';
    }

    function spawn(p, fromJar, initial) {
      if (fromJar) {
        p.x = P0.x + (Math.random() - 0.5) * jarW * 0.7;
        p.y = P0.y + Math.random() * 6;
        p.vx = (Math.random() - 0.5) * 10;
        p.vy = -(10 + Math.random() * 22);
      } else {
        var a = 0.08 + Math.random() * 1.0;
        var across = (Math.random() * 2 - 1) * (w0 + (w1 - w0) * Math.min(a, 1)) * 1.4;
        p.x = C.x + u.x * Lb * a + n.x * across;
        p.y = C.y + u.y * Lb * a + n.y * across;
        p.vx = (Math.random() - 0.5) * 8;
        p.vy = (Math.random() - 0.5) * 6;
      }
      p.bokeh = Math.random() < 0.08;
      p.r = p.bokeh ? 2.6 + Math.random() * 3.2 : 0.55 + Math.pow(Math.random(), 2) * 1.35;
      p.max = 6 + Math.random() * 8;
      p.life = initial ? Math.random() * p.max * 0.75 : 0;
      p.ph = Math.random() * 6.283;
      p.tw = 0.8 + Math.random() * 2.4;
    }

    function place() {
      var r = canvas.getBoundingClientRect();
      W = r.width;
      H = r.height;
      if (!W || !H) return;
      var d = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(W * d);
      canvas.height = Math.round(H * d);
      ctx.setTransform(d, 0, 0, d, 0, 0);
      jarW = source.offsetWidth;
      P0 = { x: source.offsetLeft + nx * source.offsetWidth, y: source.offsetTop + ny * source.offsetHeight };
      C = { x: W * 1.02, y: -H * 0.1 };
      var dx = P0.x - C.x, dy = P0.y - C.y, L = Math.sqrt(dx * dx + dy * dy);
      u = { x: dx / L, y: dy / L };
      n = { x: -u.y, y: u.x };
      Lb = L;
      w0 = W * 0.06;
      w1 = W * 0.15;
      drawBeam();
      if (!motes.length) {
        for (var i = 0; i < count; i++) { var p = {}; spawn(p, Math.random() < 0.25, true); motes.push(p); }
      }
    }

    function update(dt) {
      fx.t += dt;
      var t = fx.t;
      if (Math.floor(t / 4.6) !== fx.lastRelease) {
        fx.lastRelease = Math.floor(t / 4.6);
        for (var j = 0; j < 14 && j < motes.length; j++) spawn(motes[(Math.random() * motes.length) | 0], true, false);
      }
      for (var i = 0; i < motes.length; i++) {
        var p = motes[i];
        p.vx += (Math.sin(t * 0.6 + p.ph) * 5 - p.vx * 0.35) * dt;
        p.vy += (Math.cos(t * 0.45 + p.ph * 1.3) * 4 - p.vy * 0.3 - 1.5) * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.life += dt;
        if (p.life > p.max || p.x < -20 || p.x > W + 20 || p.y < -20 || p.y > H + 20) spawn(p, Math.random() < 0.3, false);
      }
    }

    function render() {
      ctx.clearRect(0, 0, W, H);
      var t = fx.t;
      for (var i = 0; i < motes.length; i++) {
        var p = motes[i];
        var dx = p.x - C.x, dy = p.y - C.y;
        var along = dx * u.x + dy * u.y;
        var across = Math.abs(dx * n.x + dy * n.y);
        var q = Math.max(0, Math.min(along / Lb, 1.15));
        var wAt = w0 + (w1 - w0) * Math.min(q, 1);
        var light = along > 0 ? Math.exp(-Math.pow(across / wAt, 2) * 1.4) : 0;
        var fade = Math.min(1, p.life / 1.4) * Math.min(1, (p.max - p.life) / 1.8);
        var tw = 0.62 + 0.38 * Math.sin(t * p.tw + p.ph);
        var a = fade * (0.05 + 0.95 * light) * tw * (p.bokeh ? 0.26 : 0.95);
        if (a < 0.015) continue;
        var s = p.r * 2.6;
        ctx.globalAlpha = a;
        ctx.drawImage(sprite, p.x - s, p.y - s, s * 2, s * 2);
      }
      ctx.globalAlpha = 1;
    }

    var raf = null, last = 0, frame = 0;
    function tick(now) {
      if (!fx.running) return;
      raf = requestAnimationFrame(tick);
      if (small && (frame++ & 1)) return;
      var dt = last ? Math.min((now - last) / 1000, 1 / 20) : 1 / 60;
      last = now;
      update(dt * timeScale);
      render();
    }
    fx.start = function () { if (fx.running) return; fx.running = true; last = 0; raf = requestAnimationFrame(tick); };
    fx.stop = function () { fx.running = false; if (raf) cancelAnimationFrame(raf); raf = null; };
    fx.destroy = fx.stop;
    fx.place = function () { var had = motes.length; place(); if (had) render(); };
    fx.render = render;
    fx.advance = function (steps, dt) { for (var i = 0; i < steps; i++) update(dt); };

    place();
    fx.still = reduceMotion || section.hasAttribute('data-still');
    update(0.01);
    render();
    section.classList.add('nr-show--canvas');
    section.setAttribute('data-ready', '');
    if (onReady) setTimeout(onReady, 0);
    return fx;
  }



  function initFx(section, onReady) {
    var cfg = CONFIGS[section.getAttribute('data-effect')];
    var canvas = section.querySelector('[data-nr-fluid]');
    var dropsCanvas = section.querySelector('[data-nr-drops]');
    var source = section.querySelector('[data-nr-source]');
    if (!cfg || !canvas || !source || num(section, 'data-level', 70) === 0) return null;
    var k = Math.max(0, Math.min(num(section, 'data-level', 70), 100)) / 100;
    var C = cfg(section, k);
    var o = C.o;
    o.timeScale = Math.max(10, Math.min(num(section, 'data-speed', 60), 100)) / 100;
    var fluid;
    try {
      fluid = new NRFluid(canvas, o);
    } catch (err) {
      section.classList.add('nr-show--no-webgl');
      return null;
    }
    section.classList.add('nr-show--webgl');
    var drops = dropsCanvas && C.drops ? new Drops(dropsCanvas, C.drops) : null;
    var wrap = canvas.parentNode;
    var dir = section.getAttribute('data-dir') === 'left' ? -1 : 1;
    var nx = num(section, 'data-nozzle-x', 50) / 100;
    var ny = num(section, 'data-nozzle-y', 10) / 100;
    var noz = null;

    function place() {
      fluid.resize();
      if (drops) drops.resize();
      var W = wrap.offsetWidth;
      var H = wrap.offsetHeight;
      if (!W || !H) return;
      var px = source.offsetLeft + nx * source.offsetWidth - wrap.offsetLeft;
      var py = source.offsetTop + ny * source.offsetHeight - wrap.offsetTop;
      noz = { px: px, py: py, x: px / W, y: 1 - py / H };
      fluid.glowPos = [noz.x, noz.y];
      if (C.place) C.place(fluid, noz, source, W, H, wrap);
      if (C.shift) fluid.colorRange = [noz.y + 0.04, Math.min(0.98, noz.y + 0.34)];
    }
    place();
    fluid.place = place;

    var last = -1;
    var pressTimer;
    if (!C.native) fluid.emitter = function (dt, t) {
      if (!noz) return;
      if (C.continuous) {
        C.emit(fluid, o, 1, t, 0, dt, noz, dir, drops);
        return;
      }
      var ph = t - o.firstBurst;
      if (ph < 0) return;
      var n = Math.floor(ph / o.interval);
      var local = ph - n * o.interval;
      if (n !== last) {
        last = n;
        if (C.press && !fluid.silent) {
          section.classList.add('is-pressing');
          clearTimeout(pressTimer);
          pressTimer = setTimeout(function () { section.classList.remove('is-pressing'); }, (o.burst / o.timeScale) * 1000 + 80);
        }
      }
      if (local > o.burst) return;
      var env = Math.min(1, local / 0.05) * Math.min(1, (o.burst - local) / 0.09);
      C.emit(fluid, o, env, local, n, dt, noz, dir, drops);
    };
    if (drops) {
      fluid.onStep = function (dt) { drops.update(dt); };
      fluid.onRender = function () { drops.draw(); };
    }

    var still = reduceMotion || section.hasAttribute('data-still');
    fluid.still = still;
    var at = parseFloat(section.getAttribute('data-still-at'));
    var warmSteps = still ? Math.round((o.firstBurst + (isNaN(at) ? C.still : at)) * 60) : 6;
    var warmDt = 1 / 60;
    if (C.continuous) {
      warmSteps = C.warmSteps;
      warmDt = C.warmDt;
    }
    fluid.silent = true;
    fluid.warm(warmSteps, still ? 20 : (C.continuous ? 8 : 6), warmDt, function () {
      fluid.silent = false;
      section.setAttribute('data-ready', '');
      if (onReady) onReady();
    });
    return fluid;
  }



  function init(section) {
    if (section.dataset.nrShowReady === 'true') return;
    section.dataset.nrShowReady = 'true';
    var kind = section.getAttribute('data-effect');
    var special = { dust: initDust, bubbles: initBubbles, silk: initSilk, strands: initStrands, swirl: initSilk, water: initWater, honey: initHoney };
    if (!CONFIGS[kind] && !special[kind]) return;
    var make = special[kind] || initFx;
    var fx = null;
    var visible = false;
    var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 1); };

    function sync() {
      if (!fx) return;
      if (visible && !document.hidden && !fx.still) fx.start();
      else fx.stop();
    }
    function ensure() {
      if (fx || section.dataset.nrShowTried) return;
      section.dataset.nrShowTried = 'true';
      idle(function () {
        fx = make(section, sync);
        section.nrFx = fx;
        sync();
      }, { timeout: 400 });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) ensure(); });
      }, { rootMargin: '1200px 0px' }).observe(section);
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          visible = e.isIntersecting;
          if (visible) ensure();
          if (e.intersectionRatio >= 0.1) section.classList.add('is-revealed');
          sync();
        });
      }, { threshold: [0, 0.1, 0.25] }).observe(section);
    } else {
      visible = true;
      section.classList.add('is-revealed');
      ensure();
    }

    var lastX = null;
    var lastY = null;
    section.addEventListener('pointermove', function (e) {
      if (!fx || !fx.canvas) return;
      var c = fx.canvas.getBoundingClientRect();
      var x = (e.clientX - c.left) / c.width;
      var y = 1 - (e.clientY - c.top) / c.height;
      if (x < 0 || x > 1 || y < 0 || y > 1) { lastX = null; return; }
      if (lastX !== null) fx.pointer = { x: x, y: y, dx: x - lastX, dy: y - lastY, moved: true };
      lastX = x;
      lastY = y;
    }, { passive: true });

    document.addEventListener('visibilitychange', sync);
    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () { if (fx && fx.place) { fx.place(); if (fx.still) fx.render(); } }, 150);
    });
    section.nrDestroy = function () { if (fx) fx.destroy(); fx = null; };
  }

  window.NRShowcase = {
    boot: function (scope) {
      (scope || document).querySelectorAll('[data-nr-show]').forEach(init);
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { window.NRShowcase.boot(document); });
  } else {
    window.NRShowcase.boot(document);
  }
  document.addEventListener('shopify:section:load', function (e) { window.NRShowcase.boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target && e.target.querySelector && e.target.querySelector('[data-nr-show]');
    if (el && el.nrDestroy) el.nrDestroy();
  });
})();
